import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { useForm } from "@tanstack/react-form";
import {
	createFileRoute,
	useNavigate,
	useRouteContext,
} from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
	fetchQuery,
	graphql,
	useMutation,
	useRelayEnvironment,
} from "react-relay";
import slugify from "slugify";
import { toast } from "sonner";
import { z } from "zod";
import { useOnboardingAnalytics } from "@/analytics/onboarding";
import { Heading } from "@/components/heading";
import { Icon } from "@/components/icon";
import { Image } from "@/components/image";
import { Text } from "@/components/text";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@/components/ui/input-group";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { useFileUpload } from "@/hooks/useFileUpload";
import type { onboarding_createClubMutation } from "./__generated__/onboarding_createClubMutation.graphql";
import type { onboardingGetClubSlugQuery } from "./__generated__/onboardingGetClubSlugQuery.graphql";

export const Route = createFileRoute("/_logged/onboarding/")({
	component: RouteComponent,
});

function RouteComponent() {
	const { session } = useRouteContext({ from: "__root__" });
	const environment = useRelayEnvironment();
	const [isHovering, setIsHovering] = useState(false);
	const turnstileRef = useRef<TurnstileInstance>(null);
	const navigate = useNavigate({ from: Route.fullPath });
	const { capture, trackError } = useOnboardingAnalytics("ecosystem_setup");

	const [createClub] = useMutation<onboarding_createClubMutation>(
		graphql`
			mutation onboarding_createClubMutation(
				$payload: CreateClubInput!
				$token: String!
			) {
				createClub(payload: $payload, token: $token) {
					id
					slug
					name
				}
			}
		`,
	);

	const form = useForm({
		defaultValues: {
			name: "",
			slug: "",
			photo: {
				id: "SW1hZ2VGaWxlOms5Mmk0a29xbHdta3ltOTNzaHlwYzF0cQ==",
				url: "https://assets.scarf.club/Q9E6iZOArctgfzaMPeg9OwUD2QbdaXc2RSG1kzGxBGg/w:120/q:80/f:webp/aHR0cHM6Ly9zY2FyZi1wdWJsaWMtYXNzZXRzLnMzLnVzLWVhc3QtMS5hbWF6b25hd3MuY29tL3Byb2ZpbGUtcGxhY2Vob2xkZXIucG5n",
			},
			website: "",
			token: "",
		},
		validators: {
			onChangeAsyncDebounceMs: 300,
			onChangeAsync: z.object({
				name: z.string().min(1, "Nome do Ecossistema é obrigatório"),
				slug: z
					.string()
					.min(1, "URL do Ecossistema é obrigatória")
					.refine(async (value) => {
						const data = await fetchQuery<onboardingGetClubSlugQuery>(
							environment,
							graphql`
						  query onboardingGetClubSlugQuery($slug: String!) {
                club(slug: $slug) {
                  id
                  slug
                }
              }
          `,
							{ slug: value },
						)
							.toPromise()
							.catch((_err) => undefined);

						return !data?.club;
					}, "Domínio já existente"),
				photo: z.object({
					id: z.string(),
					url: z.url(),
				}),
				website: z.url().or(z.literal("")),
				token: z.string().min(1, "Por favor, complete o captcha"),
			}),
		},
		onSubmit: async ({ value }) => {
			return new Promise((resolve, reject) => {
				createClub({
					variables: {
						payload: {
							name: value.name,
							slug: value.slug,
							logoFile: value.photo.id,
							website: value.website?.length ? value.website : undefined,
						},
						token: value.token,
					},
					onCompleted: (response, errors) => {
						if (errors?.length) {
							toast.error(errors[0].message);
							reject(new Error(errors[0].message));
							return;
						}
						capture("completed", {
							ecosystem_name: value.name,
							ecosystem_slug: value.slug,
							has_website: Boolean(value.website?.length),
							has_custom_logo:
								value.photo.id !==
								"SW1hZ2VGaWxlOms5Mmk0a29xbHdta3ltOTNzaHlwYzF0cQ==",
						});
						toast.success("Ecossistema criado com sucesso!");
						navigate({
							to: "/onboarding/$club/theme",
							params: { club: response.createClub.slug },
						});
						resolve(response);
					},
					onError: (error) => {
						trackError("submission", {
							error_message: error.message,
						});
						toast.error("Erro ao criar ecossistema. Tente novamente.");
						reject(error);
					},
				});
			});
		},
	});

	const { upload } = useFileUpload({ session });

	const { getRootProps, getInputProps, open } = useDropzone({
		noClick: true,
		accept: {
			"image/*": [],
		},
		onDrop: ([file]) => {
			if (file) {
				upload(file).then((res) => {
					form.setFieldValue("photo", { id: res.globalId, url: res.url });
				});
			}
		},
	});

	return (
		<div className="container mx-auto p-6 max-w-2xl flex flex-col">
			<Card className="p-6 gap-0">
				<div>
					<Heading>Configure seu ecossistema</Heading>

					<Text>
						Deixe seu ecosistema com a sua cara dando um nome, um ícone e
						definindo uma URL. Todos podem ser mudados depois.
					</Text>
				</div>

				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					className="mt-6"
				>
					<div className="flex flex-row items-center justify-between gap-3 py-3">
						<Field className="flex-1">
							<FieldLabel>Logo do Ecossistema</FieldLabel>
							<FieldDescription>
								Dimensões Recomendadas: 600x600px
							</FieldDescription>
						</Field>

						<form.Field name="photo.url">
							{(field) => (
								<>
									{/*biome-ignore lint/a11y/noStaticElementInteractions: expected*/}
									<div
										onMouseEnter={() => setIsHovering(true)}
										onMouseLeave={() => setIsHovering(false)}
										className="relative w-fit"
										{...getRootProps()}
									>
										<input {...getInputProps()} className="hidden" />
										<Image
											className="rounded-full aspect-square size-16 border border-dashed bg-muted object-cover"
											src={field.state.value}
										/>
										{isHovering && (
											<button
												type="button"
												onClick={() => open()}
												className="inset-0 absolute size-16 flex items-center justify-center border border-blue-600! rounded-full bg-blue-600/10 text-blue-600 border-dashed"
											>
												<Icon name="Upload" className="size-6" />
											</button>
										)}
									</div>
								</>
							)}
						</form.Field>
					</div>

					<div className="py-3">
						<form.Field
							name="name"
							listeners={{
								onChange: ({ value }) => {
									const slugMeta = form.getFieldMeta("slug");

									if (!slugMeta?.isTouched) {
										form.setFieldValue(
											"slug",
											slugify(value, {
												lower: true,
												trim: true,
												strict: true,
												locale: "pt",
											}),
										);

										form.setFieldMeta("slug", (meta) => ({
											...meta,
											isTouched: false,
										}));
									}
								},
							}}
						>
							{(field) => (
								<Field>
									<FieldLabel htmlFor={field.name}>
										Nome do Ecossistema
									</FieldLabel>
									<Input
										id={field.name}
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										placeholder="Ex: Clube do Copywriter"
										aria-invalid={
											!!(
												field.state.meta.isTouched && field.state.meta.errors[0]
											)
										}
									/>

									{field.state.meta.isTouched && field.state.meta.errors[0] ? (
										<FieldError errors={field.state.meta.errors} />
									) : null}
								</Field>
							)}
						</form.Field>
					</div>

					<div className="py-3">
						<form.Field name="slug">
							{(field) => (
								<Field>
									<FieldLabel htmlFor={field.name}>
										URL do Ecossistema
									</FieldLabel>
									<InputGroup>
										<InputGroupInput
											id={field.name}
											name={field.name}
											value={field.state.value}
											onBlur={field.handleBlur}
											onChange={(e) => field.handleChange(e.target.value)}
											placeholder="Ex: clube-do-copywriter"
											aria-invalid={
												!!(
													field.state.meta.isTouched &&
													field.state.meta.errors[0]
												)
											}
										/>

										<InputGroupAddon
											align="inline-end"
											className="text-foreground"
										>
											.scarf.club
										</InputGroupAddon>
									</InputGroup>

									{field.state.meta.isTouched && field.state.meta.errors[0] ? (
										<FieldError errors={field.state.meta.errors} />
									) : null}
								</Field>
							)}
						</form.Field>
					</div>

					<div className="py-3">
						<form.Field name="website">
							{(field) => (
								<Field>
									<FieldLabel htmlFor={field.name}>Seu Site Oficial</FieldLabel>

									<FieldDescription>
										Usamos essa página para entender o que você vende e gerar
										recomendações dentro da Scarf.
										<br />
										Prefira uma homepage, catálogo ou página que represente bem
										seu negócio.
									</FieldDescription>

									<Input
										id={field.name}
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										placeholder="https://seudominio.com.br"
										aria-invalid={
											!!(
												field.state.meta.isTouched && field.state.meta.errors[0]
											)
										}
									/>

									{field.state.meta.isTouched && field.state.meta.errors[0] ? (
										<FieldError errors={field.state.meta.errors} />
									) : null}
								</Field>
							)}
						</form.Field>
					</div>

					<Separator className="mt-3" />

					<div className="flex flex-row gap-3 items-center justify-between pt-6">
						<form.Field name="token">
							{(field) => (
								<Field>
									<Turnstile
										ref={turnstileRef}
										siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
										onSuccess={(token) => field.handleChange(token)}
										onExpire={() => {
											turnstileRef.current?.reset();
											field.handleChange("");
										}}
										lang="pt-BR"
										options={{
											theme: "light",
										}}
									/>
									{field.state.meta.isTouched && field.state.meta.errors[0] ? (
										<FieldError errors={field.state.meta.errors} />
									) : null}
								</Field>
							)}
						</form.Field>

						<form.Subscribe selector={(state) => [state.isSubmitting] as const}>
							{([isSubmitting]) => (
								<div className="pb-0 flex items-center justify-center">
									<Button
										type="submit"
										disabled={isSubmitting}
										className="flex items-center gap-2"
									>
										Criar Ecossistema
										{isSubmitting && <Spinner />}
									</Button>
								</div>
							)}
						</form.Subscribe>
					</div>
				</form>
			</Card>
		</div>
	);
}
